import zionDebug from '@fs/zion-debug'
import ZionForm, { useZionForm } from '@fs/zion-form'
import { ContentAdd } from '@fs/zion-icon'
import { BillboardButton, InfoSheetPanel, useOverlay } from '@fs/zion-ui'
import { yupResolver } from '@hookform/resolvers/yup'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import * as Yup from 'yup'
import {
  generateSls,
  getImage,
  getMostRecentFieldValue,
  getNestedOrFloatingElementById,
  getPersonName,
  useSlsHighlightsContext,
  useSlsStateContext,
} from '@fs/zion-sls'
import { useR2DispatchContext, useR2StateContext } from '../../../../providers/r2Provider/R2Context'
import PanelHeader from './PanelHeader'
import FieldSelectorOverlay from '../../../../components/generic/panels/shared/fieldChipSelector/FieldSelectorOverlay'
import { translateAgeFieldChips } from '../../../../utils/translationUtils'
import { getNextUuid } from '../../../../../shared/utils/utils'
import EditPersonFormElement from './EditPersonFormElement'
import {
  removeFieldFromValues,
  submitEditFields,
  getCreateDispatchesForSingleField,
} from './utils/editPanelFieldHelpers'
import SaveComponent from '../../../../components/generic/panels/shared/SaveComponent'
import { useOverlayContext } from '../../../../../shared/providers/overlayContextProvider/OverlayContext'
import FieldWrapper from '../ui/FieldWrapper'
import RowWrapper from '../ui/RowWrapper'
import useSetRecordHighlightsOfOneRecord from '../../hooks/useSetRecordHighlightsOfOneRecord'
import { useCollectionContextProvider } from '../../../../providers/collectionProvider/CollectionProvider'
import { FIELDS } from '../../../../providers/collectionProvider/collectionConstants'
import useUnsavedChangesOverlay from '../../../../hooks/useUnsavedChangesOverlay'
import { useSharedBrowserStateContext } from '../../../../../shared/providers/sharedBrowserProvider/SharedBrowserContext'
import { R2AppName } from '../../../../../shared/services/config'

const debug = zionDebug('rip:R2:EditAge')

const EditAge = () => {
  const [t] = useTranslation()
  const state = useSlsStateContext()
  const r2State = useR2StateContext()
  const { thumbnails } = useSharedBrowserStateContext()
  const { clearEdits, setDisplayHighlights, getDispatches } = useSlsHighlightsContext()
  const ageFieldChipData = translateAgeFieldChips(state.types)
  const ageFieldSelectorOverlay = useOverlay()
  const [ageFields, setAgeFields] = useState([])
  const r2Dispatch = useR2DispatchContext()
  const setRecordHighlightsOfOneRecord = useSetRecordHighlightsOfOneRecord()
  const currentImage = getImage(state?.images, state?.currentImageArk) || {}
  const selectedPerson = getNestedOrFloatingElementById([currentImage], r2State.selectedPersonId)
  const { restrictedFields } = useCollectionContextProvider()
  const isRestricted = restrictedFields?.includes(FIELDS.AGE)

  // Filter and process fields for the ui
  const ageElements = useMemo(() => {
    const age = selectedPerson?.subElements?.find((se) => se.elementType === 'AGE')
    return age?.subElements?.filter((field) => field?.typeData?.editable)
  }, [selectedPerson])

  // UI Information
  const heading = t('edit-age.heading', 'Age')
  const instructions = t(
    'edit-age.instructions',
    'Fix any fields that are incorrect including their highlights. Add or remove fields as necessary.'
  )
  const addMoreFields = t('edit-index.add-custom.button', 'Add or Remove {field}', { field: heading })
  const { editAgeOverlay, closeOverlay, editHighlightsOverlay, topMostPanel, inPanelStack } = useOverlayContext()
  const saveChangesOverlay = useOverlay()

  const shape = {}
  const schema = Yup.object().shape(shape)
  const methods = useZionForm({
    mode: 'all',
    resolver: yupResolver(schema),
    defaultValues: {},
  })

  useUnsavedChangesOverlay({ methods, overlay: saveChangesOverlay, debug, debugMessage: 'age subscriber called: ' })

  const resetAgeFields = useCallback(() => {
    const newFields = ageElements?.map((field) => ({
      id: field.id,
      label: field.label,
      rects: field.rects,
      fieldType: field.fieldType,
      typeData: { editable: field?.typeData?.editable || field?.editable },
    }))
    setAgeFields(newFields)
  }, [ageElements])

  useEffect(() => {
    resetAgeFields()
  }, [ageElements, resetAgeFields])

  const resetAndClose = () => {
    clearEdits()
    r2Dispatch({ type: 'REVERT_HIGHLIGHTS', value: {} })
    closeOverlay('editAge')
    resetAgeFields()
  }

  // The back arrow on the panel was clicked
  const userClosedPanel = () => {
    if (methods.formState.isDirty) saveChangesOverlay.open()
    else {
      resetAndClose()
    }
  }

  const handleSubmit = (values) => {
    const dispatches = submitEditFields({
      ...state,
      ...r2State,
      inPanelStack,
      values,
      formState: methods.formState,
      currentImageArk: state.currentImageArk,
    }).concat(
      getDispatches({
        mainImageArk: state.currentImageArk,
        splitScreenImageArk: thumbnails?.[r2State.referenceFocusedIndex]?.ark,
      })
    )
    return { sls: generateSls(dispatches), dispatches }
  }

  /**
   * This useEffect prepares dispatches to create a field in SLS when:
   * 1) the highlight being selected for the field is in use by one or more other fields
   * 2) the editSharedHighlightPage2 panel comes into view
   * 3) the field does not yet exist in SLS
   * The dispatches are saved in R2 state and use in editSharedHighlightPage2.js when preparing and submitting dispatches
   */
  useEffect(() => {
    const existingField = getNestedOrFloatingElementById(state.images, r2State.highlightsFieldInfo.id)
    if (topMostPanel() === 'editSharedHighlightPage2' && r2State.editDispatches.length === 0 && !existingField) {
      const dispatchesForHighlightField = getCreateDispatchesForSingleField({
        ...state,
        ...r2State,
        inPanelStack,
        values: methods.getValues(),
        formState: methods.formState,
        currentImageArk: state.currentImageArk,
      })
      r2Dispatch({
        type: 'SET_EDIT_DISPATCHES',
        value: dispatchesForHighlightField,
      })
    }
  }, [inPanelStack, methods, r2Dispatch, r2State, state, topMostPanel])

  const finished = () => {
    closeOverlay('editAge')
    resetAgeFields()
  }

  editAgeOverlay.onClose = () => {
    if (methods.formState.isDirty) {
      methods.reset()
      resetAgeFields()
    }
  }

  const { reset, setValue } = methods

  useEffect(() => {
    const formDefaultValues = {}
    if (Array.isArray(ageElements)) {
      ageElements.forEach((element) => {
        formDefaultValues[element.id] = getMostRecentFieldValue(element) || '' // Set a string value instead of undefined so dirty checking will work properly.
      })
    }
    reset(formDefaultValues)
  }, [ageElements, reset])

  useEffect(() => {
    if (editAgeOverlay.isOpen && topMostPanel() === 'editAge' && !editHighlightsOverlay.isOpen) {
      const filteredAgeFields = state.types.fields
        .filter((field) => field.elementTypes?.includes('AGE'))
        .map((field) => field.fieldType)
      const splitScreenImageArk = thumbnails?.[r2State.referenceFocusedIndex]?.ark
      const elementIds = [r2State.selectedPersonId]
      setDisplayHighlights({
        mainPartitionKey: state.currentImageArk,
        splitScreenPartitionKey: splitScreenImageArk,
        fieldTypes: filteredAgeFields,
        elementIds,
      })
      setRecordHighlightsOfOneRecord()
    }
  }, [
    setDisplayHighlights,
    editAgeOverlay.isOpen,
    editHighlightsOverlay.isOpen,
    r2State.editHighlights,
    r2State.referenceEditHighlights,
    r2State.referenceFocusedIndex,
    r2State.selectedPersonId,
    r2State.splitScreen,
    setRecordHighlightsOfOneRecord,
    state.currentImageArk,
    thumbnails,
    state.types.fields,
    topMostPanel,
  ])

  const { handleFieldChange, handleOnFocus } = {
    handleFieldChange: () => {
      r2Dispatch({ type: 'SET_ACTIVE_FIELD_ID', value: '' })
    },
    handleOnFocus: (el) => {
      const activeFieldId = el.subElements ? el?.subElements?.[0]?.id : el.id
      r2Dispatch({ type: 'SET_ACTIVE_FIELD_ID', value: activeFieldId })
    },
  }

  const handleChipSelection = (chips, lockedChips) => {
    const outFieldsThatStayed = (field) =>
      !chips.map(({ id }) => id).includes(field.id) && !lockedChips.map(({ id }) => id).includes(field.id)
    const newFields = ageFields ? [...ageFields] : []

    if (ageFields) {
      const removeField = (field) => {
        removeFieldFromValues(field, methods)
        const index = newFields.findIndex((f) => f.id === field.id)
        newFields.splice(index, 1)
      }

      ageFields.filter(outFieldsThatStayed).forEach(removeField)
    }

    const newChips = chips.filter((chip) => !chip.id)
    if (newChips) {
      newChips.reverse() // Chips are added at the front of the array, so reversing them here gets back to the order they were clicked
      newChips.forEach((chip) => {
        const newField = {
          label: chip.label,
          fieldType: chip.value.split(':')[0],
          id: getNextUuid(),
          added: true,
        }
        newFields.push(newField)
        setValue(newField.id, '')
        setValue(`${newField.id}_fieldType`, chip.value.split(':')[0])
      })
    }

    setAgeFields(newFields)
  }

  return (
    <ZionForm onSubmit={handleSubmit} methods={methods} id="edit-age">
      <InfoSheetPanel
        {...{ ...editAgeOverlay, close: userClosedPanel }}
        headingColor="green"
        title={t('edit-index.edit-person.heading', 'Edit {personName}', {
          personName: getPersonName({ person: selectedPerson }),
        })}
        footer={
          <SaveComponent
            saveChangesOverlay={saveChangesOverlay}
            onSubmit={handleSubmit}
            finished={finished}
            canceled={resetAndClose}
            appName={R2AppName}
            personId={r2State.userId}
            formName="edit-age"
          />
        }
      >
        <PanelHeader heading={heading} caption={instructions} />
        <RowWrapper>
          {ageFields?.map((field) => (
            <FieldWrapper key={field.id}>
              <EditPersonFormElement
                field={field}
                handleOnFocus={handleOnFocus}
                handleFieldChange={handleFieldChange}
                key={field.id}
              />
            </FieldWrapper>
          ))}
          <FieldWrapper>
            <BillboardButton
              data-testid="add-or-remove-age"
              Icon={ContentAdd}
              color="yellow"
              emphasis="low"
              disabled={isRestricted}
              onClick={ageFieldSelectorOverlay.handleClick}
              aligny="nextToInput"
            >
              {addMoreFields}
            </BillboardButton>
          </FieldWrapper>
        </RowWrapper>
        <FieldSelectorOverlay
          title={addMoreFields}
          disabled={isRestricted}
          chipData={[
            {
              title: t('edit-index.chips.age-fields.title', 'Age Fields'),
              data: ageFieldChipData,
              key: 'additionalFields',
            },
          ]}
          fieldData={ageFields}
          handleSelection={handleChipSelection}
          overlay={ageFieldSelectorOverlay}
          allowSelectingMultiple={false}
        />
      </InfoSheetPanel>
    </ZionForm>
  )
}

export default EditAge
